import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  where,
  Timestamp,
} from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { startOfWeek, endOfWeek, format } from "date-fns";
import { ptBR } from "date-fns/locale";

const RankingPage = () => {
  const [searchParams] = useSearchParams();
  const temaParam = searchParams.get("tema");
  const mode = temaParam === "ecologia" ? "ecologia" : "cultura";

  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isWeekly, setIsWeekly] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRanking = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Definir a coleção de acordo com o modo
        const collectionName = mode === "ecologia" ? "ecoRanking" : "ranking";
        const rankingRef = collection(db, collectionName);
        let q;

        if (isWeekly) {
          const now = new Date();
          const start = startOfWeek(now, { weekStartsOn: 1 }); // Segunda-feira
          const end = endOfWeek(now, { weekStartsOn: 1 }); // Domingo
          
          // Converter para Timestamp do Firestore
          const startTimestamp = Timestamp.fromDate(start);
          const endTimestamp = Timestamp.fromDate(end);

          q = query(
            rankingRef,
            where("date", ">=", startTimestamp),
            where("date", "<=", endTimestamp)
          );
        } else {
          // Para ranking geral, buscar todos os registros
          q = query(rankingRef, orderBy("date", "desc"));
        }

        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Agregar pontuações por usuário
        const aggregatedData = aggregateUserScores(data, isWeekly);
        
        // Ordenar por pontuação total
        const sortedData = aggregatedData.sort((a, b) => b.totalScore - a.totalScore);
        
        // Limitar a 20 posições
        setRanking(sortedData.slice(0, 20));
        
      } catch (error) {
        console.error("Erro ao buscar ranking:", error);
        setError("Erro ao carregar o ranking. Tente novamente.");
      } finally {
        setLoading(false);
      }
    };

    fetchRanking();
  }, [isWeekly, mode]);

  // Função para agregar pontuações do mesmo usuário
  const aggregateUserScores = (data, isWeekly) => {
    const userMap = new Map();

    data.forEach((record) => {
      const userId = record.uid || record.name; // Usar UID se disponível, senão nome
      const userName = record.name || "Anônimo";
      
      if (userMap.has(userId)) {
        const existing = userMap.get(userId);
        existing.totalScore += record.totalScore || 0;
        existing.level = Math.max(existing.level || 1, record.level || 1);
        existing.attempts = (existing.attempts || 0) + 1;
        // Manter a data mais recente
        if (record.date && (!existing.date || record.date.toDate() > existing.date.toDate())) {
          existing.date = record.date;
        }
      } else {
        userMap.set(userId, {
          uid: userId,
          name: userName,
          totalScore: record.totalScore || 0,
          level: record.level || 1,
          mode: record.mode || mode,
          date: record.date,
          attempts: 1,
        });
      }
    });

    return Array.from(userMap.values());
  };

  const getMedalEmoji = (position) => {
    switch (position) {
      case 0:
        return "🥇";
      case 1:
        return "🥈";
      case 2:
        return "🥉";
      default:
        return `${position + 1}º`;
    }
  };

  const getRowStyle = (position) => {
    const base = { ...styles.row };
    if (position === 0) return { ...base, backgroundColor: "#FFF9C4" };
    if (position === 1) return { ...base, backgroundColor: "#E0E0E0" };
    if (position === 2) return { ...base, backgroundColor: "#FFE0B2" };
    return base;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch (error) {
      return "N/A";
    }
  };

  const getWeekRange = () => {
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const end = endOfWeek(now, { weekStartsOn: 1 });
    return `${format(start, "dd/MM", { locale: ptBR })} - ${format(end, "dd/MM", { locale: ptBR })}`;
  };

  return (
    <main style={styles.container}>
      <section>
        <h1 style={styles.title}>
          🏆 {isWeekly ? "Ranking Semanal" : "Ranking Geral"} -{" "}
          {mode === "ecologia" ? "Ecologia" : "Cultura"}
        </h1>
        
        {isWeekly && (
          <p style={styles.weekInfo}>
            Semana: {getWeekRange()}
          </p>
        )}

        <button
          onClick={() => setIsWeekly(!isWeekly)}
          style={styles.toggleButton}
          aria-label="Alternar entre ranking semanal e geral"
        >
          Ver {isWeekly ? "Ranking Geral" : "Ranking Semanal"}
        </button>
      </section>

      {error && (
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={styles.retryButton}
          >
            Tentar Novamente
          </button>
        </div>
      )}

      {loading ? (
        <div style={styles.loadingContainer}>
          <p style={styles.loadingText}>Carregando ranking...</p>
          <div style={styles.spinner}></div>
        </div>
      ) : ranking.length === 0 ? (
        <div style={styles.noDataContainer}>
          <p style={styles.noDataText}>
            Nenhuma pontuação disponível {isWeekly ? "esta semana" : "ainda"}.
          </p>
          <p style={styles.encourageText}>
            Jogue e entre no ranking! 🎮
          </p>
        </div>
      ) : (
        <section style={styles.tableContainer}>
          <table
            style={styles.table}
            aria-label="Tabela de ranking de jogadores"
          >
            <thead>
              <tr>
                <th style={styles.th}>Posição</th>
                <th style={styles.th}>Jogador</th>
                <th style={styles.th}>Maior Nível</th>
                <th style={styles.th}>Pontuação Total</th>
                {!isWeekly && <th style={styles.th}>Última Jogada</th>}
              </tr>
            </thead>
            <tbody>
              {ranking.map((player, index) => (
                <tr key={player.uid || player.id} style={getRowStyle(index)}>
                  <td
                    style={{
                      ...styles.td,
                      fontWeight: index < 3 ? "bold" : "normal",
                    }}
                  >
                    {getMedalEmoji(index)}
                  </td>
                  <td style={styles.td}>
                    {player.name || "Anônimo"}
                    {player.attempts > 1 && (
                      <span style={styles.attemptsBadge}>
                        ({player.attempts} jogadas)
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>{player.level || 1}</td>
                  <td style={styles.td}>
                    <strong>{player.totalScore} pts</strong>
                  </td>
                  {!isWeekly && (
                    <td style={styles.td}>
                      {formatDate(player.date)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <button
        style={styles.button}
        onClick={() => navigate("/home")}
        aria-label="Voltar ao início"
      >
        Voltar ao Início
      </button>
    </main>
  );
};

const styles = {
  container: {
    textAlign: "center",
    padding: "20px",
    backgroundColor: "#fafafa",
    minHeight: "100vh",
    fontFamily: "'Segoe UI', sans-serif",
  },
  title: {
    fontSize: "clamp(1.5rem, 2vw, 2.5rem)",
    color: "#d32f2f",
    marginBottom: "10px",
  },
  weekInfo: {
    fontSize: "1rem",
    color: "#666",
    marginBottom: "15px",
    fontStyle: "italic",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: "40px",
  },
  loadingText: {
    fontSize: "1.2rem",
    color: "#555",
    marginBottom: "20px",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #d32f2f",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  noDataContainer: {
    marginTop: "40px",
  },
  noDataText: {
    fontSize: "1.2rem",
    color: "#777",
    marginBottom: "10px",
  },
  encourageText: {
    fontSize: "1.1rem",
    color: "#d32f2f",
    fontWeight: "bold",
  },
  errorContainer: {
    marginTop: "20px",
    padding: "20px",
    backgroundColor: "#ffebee",
    borderRadius: "8px",
    border: "1px solid #f44336",
  },
  errorText: {
    color: "#d32f2f",
    fontSize: "1.1rem",
    marginBottom: "15px",
  },
  retryButton: {
    backgroundColor: "#d32f2f",
    color: "white",
    border: "none",
    borderRadius: "6px",
    padding: "8px 16px",
    cursor: "pointer",
    fontSize: "0.9rem",
  },
  toggleButton: {
    backgroundColor: "#4caf50",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    cursor: "pointer",
    marginBottom: "20px",
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "clamp(0.8rem, 1vw, 1rem)",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
    transition: "all 0.3s ease",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    marginTop: "20px",
  },
  table: {
    width: "90%",
    maxWidth: "900px",
    minWidth: "600px",
    margin: "auto",
    borderCollapse: "collapse",
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    overflow: "hidden",
  },
  th: {
    backgroundColor: "#d32f2f",
    color: "white",
    padding: "12px",
    fontSize: "clamp(0.85rem, 1vw, 1.1rem)",
    textTransform: "uppercase",
  },
  td: {
    padding: "12px",
    borderBottom: "1px solid #ddd",
    fontSize: "clamp(0.8rem, 1vw, 1rem)",
  },
  row: {
    transition: "background-color 0.3s ease",
  },
  attemptsBadge: {
    fontSize: "0.75rem",
    color: "#666",
    marginLeft: "5px",
  },
  button: {
    marginTop: "30px",
    padding: "12px 24px",
    fontSize: "clamp(1rem, 1.2vw, 1.2rem)",
    color: "white",
    backgroundColor: "#d32f2f",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "bold",
    textTransform: "uppercase",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
    transition: "background-color 0.3s",
  },
};

// Adicionar CSS para animação do spinner
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);

export default RankingPage;
